import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PaginatedResponse, PressReleaseLight } from '@finance-lab/utils/types'
import { PressReleaseApi } from '../../../../api/PressReleasesApi'
import Card from '@mui/material/Card'
import List from '@mui/material/List'
import ListItemText from '@mui/material/ListItemText'
import ListItemButton from '@mui/material/ListItemButton'
import { Link } from 'react-router'
import { getPressReleaseLink } from '../../../../utils/get-press-release-link'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { PressReleasesFilter } from '../../../components/press-releases/PressReleasesFilter'
import { useFilterSettings, useResetPaginationOnFilterChange } from '../../../components/press-releases/utils'
import Divider from '@mui/material/Divider'
import LinearProgress from '@mui/material/LinearProgress'
import IssueTypeLabels, { HasWarrantLabel, KVLabel } from '../../../../components/entities/IssueTypeLabels'
import Stack from '@mui/material/Stack'
import { formatDateYYYYMMDDhhmm } from '../../../../packages/utils/numberFormatters'
import { useAppLocale } from '../../../../i18n/localeUtils'

const pressReleaseApi = new PressReleaseApi()

export const CompanyPressReleases = ({ companyId, currentPrId }: { companyId: string; currentPrId?: string }) => {
    const { t } = useTranslation()
    const { language: locale } = useAppLocale()
    const [pageInfo, setPageInfo] = useState<PaginatedResponse<Omit<PressReleaseLight, 'body'>>>({
        totalCount: 0,
        data: [],
    })
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    })
    const isFirstPage = (paginationModel.page + 1) * paginationModel.pageSize > pageInfo.totalCount
    const [isLoading, setIsLoading] = useState(false)
    const [hasError, setHasError] = useState(false)
    const { endDate, searchValue, contentType } = useFilterSettings()
    useResetPaginationOnFilterChange(setPaginationModel, { endDate, searchValue, contentType })

    useEffect(() => {
        const fetchData = async () => {
            try {
                const { data, totalCount } = await pressReleaseApi.allPressReleases(paginationModel, {
                    companyId,
                    maxPublicationDate: endDate ?? undefined,
                    contentType,
                    search: searchValue,
                    preferredLanguage: locale,
                })

                setPageInfo({ data: data.map(({ body, ...pr }) => pr), totalCount })
            } catch (err) {
                setHasError(true)
            } finally {
                setIsLoading(false)
            }
        }

        void fetchData()
        setIsLoading(true)
        setHasError(false)
    }, [companyId, contentType, endDate, paginationModel, locale, searchValue])

    return (
        <Card>
            <CardContent sx={{ padding: 0 }}>
                <Typography variant={'h4'} sx={{ margin: 4, marginBottom: 2 }}>
                    {t('pages.companies.pressReleases')}
                </Typography>

                <Box sx={{ marginX: 4 }}>
                    <PressReleasesFilter isSlim />
                </Box>

                <Divider sx={{ marginTop: 4 }} />
                <LinearProgress sx={{ visibility: isLoading ? 'visible' : 'hidden' }} />

                {hasError && <p>{t('pages.companies.pressRelease.error')}</p>}

                <List>
                    {pageInfo.data?.map((pr) => (
                        <ListItemButton
                            key={pr.id}
                            alignItems='flex-start'
                            selected={pr.id === currentPrId || pr.translations.some((t) => t.prId === currentPrId)}
                            LinkComponent={Link}
                            to={{
                                pathname: getPressReleaseLink(companyId, pr.id),
                                search: new URLSearchParams({
                                    endDate,
                                    contentType,
                                    ...(searchValue ? { search: searchValue } : {}),
                                }).toString(),
                            }}
                        >
                            <ListItemText
                                disableTypography
                                primary={
                                    <Box
                                        component='div'
                                        title={pr.title}
                                        sx={{
                                            typography: 'body1',
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                        {pr.title}
                                    </Box>
                                }
                                secondary={
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            alignItems: 'center',
                                            gap: 1,
                                            rowGap: 0.5,
                                        }}
                                    >
                                        <Typography
                                            component='span'
                                            variant='body2'
                                            sx={{
                                                color: 'text.secondary',
                                            }}
                                        >
                                            {formatDateYYYYMMDDhhmm(pr.publishedAt, { locale })}
                                        </Typography>
                                        {pr.relatedTransactions.length > 0 ? (
                                            <Stack
                                                direction='row'
                                                useFlexGap
                                                sx={{
                                                    flexWrap: 'wrap',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    paddingLeft: 2,
                                                }}
                                            >
                                                {pr.relatedTransactions.map((t) => {
                                                    if (t.entityType === 'Convertible') {
                                                        return <KVLabel key={t.id} />
                                                    } else if (t.entityType === 'Issue') {
                                                        return (
                                                            <IssueTypeLabels
                                                                issue={{ ...t, type: t.issueType }}
                                                                key={t.id}
                                                                isSingleLabel
                                                            />
                                                        )
                                                    } else if (t.entityType === 'Warrant') {
                                                        return <HasWarrantLabel key={t.id} />
                                                    }
                                                    return null
                                                })}
                                            </Stack>
                                        ) : null}
                                    </Box>
                                }
                            />
                        </ListItemButton>
                    ))}
                </List>

                <Box sx={{ display: 'flex', paddingX: 2 }}>
                    {!isFirstPage && (
                        <Button
                            onClick={() => setPaginationModel({ ...paginationModel, page: paginationModel.page + 1 })}
                            sx={{ justifySelf: 'flex-start' }}
                        >
                            {t('site.pagination.previous')}
                        </Button>
                    )}
                    {paginationModel.page > 0 && (
                        <Button
                            onClick={() => setPaginationModel({ ...paginationModel, page: paginationModel.page - 1 })}
                            sx={{ marginLeft: 'auto' }}
                        >
                            {t('site.pagination.later')}
                        </Button>
                    )}
                </Box>
            </CardContent>
        </Card>
    )
}
